import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
    fetchApiDelete,
    fetchApiGet,
    fetchApiPost,
    fetchApiPut,
} from "./utils";

const ListRunsParamValidation = z.object({
    locationId: z.string().uuid()
})

const ListRunHoursParamValidation = z.object({
    locationId: z.string().uuid(),
    runId: z.string().uuid(),
})

export interface DestinationHoursLocation {
      id: string;
      name: string;
      alias?: string;
      link: string;

      is_enabled: boolean;
      is_visible: boolean;

      last_fetched?: Date | string;
      last_parsed?: Date | string;
      last_ingest?: Date | string;
}

export interface DestinationHoursLocationRun {
      id: string;
      location_id: string;
      location_name: string;
      run_id: string;
      run_at: Date | string;

      prompt_version: string;
}

export interface DestinationHoursRunHours {
    location_id: string;
    location_name: string;
    run_id: string;
    run_at: Date;
    prompt_version: string;
    location_hours: {
        original_location_name: string;
        location_name: string;
        operating_hours: {
            original_group_name: string;
            group_name: string;
            original_hours: {
                date?: string;
                range?: string;
                open?: string;
                close?: string;
                note?: string;
            }[];
            hours_info: {
                block_name: string;
                date?: string;
                range?: string;
                open?: string;
                close?: string;
                note?: string;
            }[];
        }[];
    }[]
}

export interface DestinationCompareHours {
  previous: DestinationHoursRunHours[];
  requested: DestinationHoursRunHours[];
}

export function useListLocations() {
    return useQuery({
        queryKey: ["destinationHours", "locations"],
        queryFn: () => fetchApiGet<DestinationHoursLocation[]>(`destination/hours/locations`),
        retry: 1,
    });
}

export function useListLocationRuns({ locationId }: { locationId?: string }) {
    return useQuery({
        queryKey: ["destinationHours", "location", locationId || ""],
        queryFn: () => fetchApiGet<DestinationHoursLocationRun[]>(`destination/hours/locations/${locationId}/runs`),
        enabled: !!locationId,
    });
}

export function useListRunHours({ locationId, runId }: { locationId?: string; runId?: string; }) {
    return useQuery({
        queryKey: ["destinationHours", "location", locationId || ""],
        queryFn: () => fetchApiGet<DestinationHoursLocationRun[]>(`destination/hours/locations/${locationId}/runs`),
        enabled: !!locationId,
    });
}

export function useListRunHoursPaired({ locationId, runId }: { locationId?: string; runId?: string; }) {
    return useQuery({
        queryKey: ["destinationHours", "pair", "runs", locationId || "", runId || ""],
        queryFn: () => fetchApiGet<DestinationCompareHours>(`destination/hours/locations/${locationId}/runs/${runId}/pair`),
        enabled: !!locationId && !!runId,
    });
}
